import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, Input, message, Space, Tabs, Typography, Alert, Radio, Select, Switch } from 'antd';
import {
  SettingOutlined,
  LinkOutlined,
  SafetyOutlined,
  TeamOutlined,
  ExperimentOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { AiSparkIcon } from '../../shared/components/AiSparkIcon.tsx';
import { useAuth } from '../auth/AuthContext.tsx';
import { getPlatformSettings, updatePlatformSettings, fetchLlmModels, pingLlmAdmin } from './api.ts';
import { KeycloakUsersCard } from './KeycloakUsersCard.tsx';
import { HermesStandsCard } from '../hermes/HermesStandsCard.tsx';
import { TelegramAccountsCard } from '../telegram/TelegramAccountsCard.tsx';
import type { PlatformSettingsResponse } from '@lens/shared';
import { spacing, radius, shadows } from '../../shared/theme/tokens.ts';
import { useDocumentTitle } from '../../shared/hooks/useDocumentTitle.ts';

const { Title, Text } = Typography;

/**
 * Все разделы настроек одной ширины и тянутся на всю доступную ширину экрана —
 * узкая колонка посреди широкого монитора оставляла пустые поля по бокам.
 */
const SECTION_WIDTH = '100%';

/** Ширина рельса с разделами; по ней же выравнивается кнопка «Сохранить». */
const TAB_RAIL_WIDTH = 210;

/** Отступ содержимого вкладки от рельса (padding вкладки в AntD). */
const TAB_CONTENT_GAP = 24;

/** Разделы с полями общей формы настроек — только на них нужна кнопка сохранения. */
const FORM_TABS = ['jira', 'sso', 'llm'];

/** Бренд-капсула с иконкой — единый акцент со spaces/dictionaries. */
function IconBadge({ children, size = 40 }: { children: React.ReactNode; size?: number }): JSX.Element {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        borderRadius: radius.md,
        background: 'var(--color-primary-bg)',
        color: 'var(--color-primary)',
        fontSize: size >= 40 ? 18 : 14,
        flexShrink: 0,
      }}
    >
      {children}
    </span>
  );
}

export function AdminSettingsPage(): JSX.Element {
  useDocumentTitle('Настройки платформы');
  const { user } = useAuth();
  const [settings, setSettings] = useState<PlatformSettingsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Jira fields
  const [jiraMode, setJiraMode] = useState<'test' | 'prod'>('test');
  const [jiraUrl, setJiraUrl] = useState('');
  const [jiraUrlTest, setJiraUrlTest] = useState('');

  // LLM fields (ключ — персональный, задаётся в профиле пользователя)
  const [llmBaseUrl, setLlmBaseUrl] = useState('');
  const [llmModel, setLlmModel] = useState('');
  const [llmModels, setLlmModels] = useState<string[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [llmPinging, setLlmPinging] = useState(false);
  const [llmPingResult, setLlmPingResult] = useState<{ ok: boolean; model?: string; error?: string } | null>(null);

  // SSO / Keycloak fields
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [ssoConfigured, setSsoConfigured] = useState(false);
  const [ssoRoleCatalog, setSsoRoleCatalog] = useState<string[]>([]);
  const [ssoAccessRole, setSsoAccessRole] = useState('');
  const [ssoAdminRole, setSsoAdminRole] = useState('');
  const [ssoIssuer, setSsoIssuer] = useState('');
  const [ssoClientId, setSsoClientId] = useState('');
  const [ssoRedirectUri, setSsoRedirectUri] = useState('');
  const [ssoPostLogoutUri, setSsoPostLogoutUri] = useState('');
  const [ssoHasClientSecret, setSsoHasClientSecret] = useState(false);
  const [ssoClientSecret, setSsoClientSecret] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('jira');

  useEffect(() => {
    void loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getPlatformSettings();
      setSettings(data);
      setJiraMode(data.jiraMode);
      setJiraUrl(data.jiraBaseUrl ?? '');
      setJiraUrlTest(data.jiraBaseUrlTest ?? '');
      setLlmBaseUrl(data.llmBaseUrl ?? '');
      setLlmModel(data.llmModelEffective ?? '');
      setSsoEnabled(data.ssoEnabled);
      setSsoConfigured(data.ssoConfigured);
      setSsoRoleCatalog(data.ssoRoleCatalog);
      setSsoAccessRole(data.ssoAccessRole);
      setSsoAdminRole(data.ssoAdminRole);
      setSsoIssuer(data.ssoIssuer ?? '');
      setSsoClientId(data.ssoClientId ?? '');
      setSsoRedirectUri(data.ssoRedirectUri ?? '');
      setSsoPostLogoutUri(data.ssoPostLogoutUri ?? '');
      setSsoHasClientSecret(data.ssoHasClientSecret);
      setSsoClientSecret('');
    } catch (err: any) {
      setError(err.message ?? 'Не удалось загрузить настройки');
    } finally {
      setLoading(false);
    }
  };

  // Есть ли несохранённые правки реквизитов Keycloak: загрузка пользователей
  // ходит в Admin API по сохранённым значениям, а не по тому, что в форме.
  const ssoFormDirty = Boolean(
    settings &&
      (ssoIssuer.trim() !== (settings.ssoIssuer ?? '') ||
        ssoClientId.trim() !== (settings.ssoClientId ?? '') ||
        ssoClientSecret.trim() !== ''),
  );

  const loadModels = async () => {
    setModelsLoading(true);
    try {
      const models = await fetchLlmModels({
        baseUrl: llmBaseUrl.trim() || undefined,
      });
      setLlmModels(models);
    } catch (err: any) {
      void message.warning(err?.message ?? 'Не удалось загрузить список моделей');
    } finally {
      setModelsLoading(false);
    }
  };

  const handleSave = async () => {
    const trimmedProd = jiraUrl.trim();
    const trimmedTest = jiraUrlTest.trim();
    const trimmedLlmUrl = llmBaseUrl.trim();
    if (trimmedProd && !/^https?:\/\/.+/.test(trimmedProd)) {
      void message.error('Введите корректный URL для прод Jira (https://...)');
      return;
    }
    if (trimmedTest && !/^https?:\/\/.+/.test(trimmedTest)) {
      void message.error('Введите корректный URL для тест Jira (http(s)://...)');
      return;
    }
    if (trimmedLlmUrl && !/^https?:\/\/.+/.test(trimmedLlmUrl)) {
      void message.error('Введите корректный URL для LLM (http(s)://...)');
      return;
    }
    // SSO: выбранные роли обязаны присутствовать в каталоге, иначе гейт
    // никогда не сработает (в токене такой роли не будет).
    if (ssoAccessRole && !ssoRoleCatalog.includes(ssoAccessRole)) {
      void message.error('Роль доступа должна быть из каталога ролей');
      return;
    }
    if (ssoAdminRole && !ssoRoleCatalog.includes(ssoAdminRole)) {
      void message.error('Роль админа должна быть из каталога ролей');
      return;
    }
    // Расчёт «будет ли настроен» с учётом только что введённых полей.
    const willBeConfigured = Boolean(
      ssoIssuer.trim() &&
        ssoClientId.trim() &&
        ssoRedirectUri.trim() &&
        (ssoHasClientSecret || ssoClientSecret.trim()),
    );
    if (ssoEnabled && !willBeConfigured) {
      void message.warning(
        'SSO включён, но реквизиты клиента Keycloak заполнены не полностью — вход работать не будет',
      );
    }

    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        jiraMode,
        jiraBaseUrl: trimmedProd || null,
        jiraBaseUrlTest: trimmedTest || null,
        llmBaseUrl: trimmedLlmUrl || null,
        llmModel: llmModel.trim() || null,
        ssoEnabled,
        ssoRoleCatalog,
        ssoAccessRole: ssoAccessRole || undefined,
        ssoAdminRole: ssoAdminRole || undefined,
        ssoIssuer: ssoIssuer.trim() || null,
        ssoClientId: ssoClientId.trim() || null,
        ssoRedirectUri: ssoRedirectUri.trim() || null,
        ssoPostLogoutUri: ssoPostLogoutUri.trim() || null,
        // секрет шлём только если ввели новый (write-only)
        ...(ssoClientSecret.trim() ? { ssoClientSecret: ssoClientSecret.trim() } : {}),
      };

      const data = await updatePlatformSettings(payload as any);
      setSettings(data);
      setJiraMode(data.jiraMode);
      setJiraUrl(data.jiraBaseUrl ?? '');
      setJiraUrlTest(data.jiraBaseUrlTest ?? '');
      setLlmBaseUrl(data.llmBaseUrl ?? '');
      setLlmModel(data.llmModelEffective ?? '');
      setSsoEnabled(data.ssoEnabled);
      setSsoConfigured(data.ssoConfigured);
      setSsoRoleCatalog(data.ssoRoleCatalog);
      setSsoAccessRole(data.ssoAccessRole);
      setSsoAdminRole(data.ssoAdminRole);
      setSsoIssuer(data.ssoIssuer ?? '');
      setSsoClientId(data.ssoClientId ?? '');
      setSsoRedirectUri(data.ssoRedirectUri ?? '');
      setSsoPostLogoutUri(data.ssoPostLogoutUri ?? '');
      setSsoHasClientSecret(data.ssoHasClientSecret);
      setSsoClientSecret('');
      void message.success('Настройки сохранены');
    } catch (err: any) {
      void message.error(err.message ?? 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  if (!user?.isPlatformAdmin) {
    return (
      <div style={{ width: '100%', padding: `${spacing.lg}px ${spacing.base}px` }}>
        <Alert type="error" message="Доступ запрещён. Эта страница доступна только администраторам платформы." />
      </div>
    );
  }

  return (
    <div style={{ padding: `${spacing.lg}px ${spacing.base}px` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md, marginBottom: spacing.lg }}>
        <IconBadge>
          <SettingOutlined />
        </IconBadge>
        <Title level={1} style={{ fontSize: 22, margin: 0 }}>
          Настройки платформы
        </Title>
      </div>

      {error && <Alert type="error" message={error} style={{ marginBottom: spacing.base }} />}

      <Tabs
        tabPosition="left"
        activeKey={activeTab}
        onChange={setActiveTab}
        size="large"
        style={{ minHeight: 420 }}
        tabBarStyle={{ minWidth: TAB_RAIL_WIDTH }}
        items={[
          {
            key: 'jira',
            label: (
              <span>
                <LinkOutlined style={{ marginRight: spacing.xs }} />
                Jira
              </span>
            ),
            children: (
              <div style={{ maxWidth: SECTION_WIDTH }}>
              <Card
                title={
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: spacing.sm }}>
                    <IconBadge size={28}>
                      <LinkOutlined />
                    </IconBadge>
                    Jira-интеграция
                  </span>
                }
                loading={loading}
                style={{
                  width: '100%',
                  marginBottom: spacing.base,
                  borderRadius: radius.lg,
                  boxShadow: shadows.card,
                }}
              >
                <div style={{ marginBottom: spacing.lg }}>
                  <Text strong style={{ display: 'block', marginBottom: spacing.sm }}>Режим</Text>
                  <Radio.Group
                    value={jiraMode}
                    onChange={(e) => setJiraMode(e.target.value)}
                    optionType="button"
                    buttonStyle="solid"
                  >
                    <Radio.Button value="test">Тест</Radio.Button>
                    <Radio.Button value="prod">Прод</Radio.Button>
                  </Radio.Group>
                  {settings && (
                    <Text
                      style={{ display: 'block', marginTop: spacing.xs, fontSize: 12, color: 'var(--color-text-secondary)' }}
                    >
                      Активный URL: {settings.jiraBaseUrlEffective ?? '—'}
                    </Text>
                  )}
                </div>

                <div style={{ marginBottom: spacing.base }}>
                  <Text style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                    URL тестовой Jira
                  </Text>
                  <Input
                    placeholder="http://jira-dev-slt.moscow.alfaintra.net"
                    value={jiraUrlTest}
                    onChange={(e) => setJiraUrlTest(e.target.value)}
                    data-testid="jira-base-url-test-input"
                    style={{ marginTop: spacing.xs }}
                    addonBefore={
                      jiraMode === 'test'
                        ? <span style={{ color: 'var(--color-success)', fontWeight: 600 }}>● АКТИВ</span>
                        : undefined
                    }
                    autoComplete="off"
                    data-lpignore="true"
                    data-1p-ignore="true"
                    data-form-type="other"
                    name="lens-jira-url-test"
                    type="url"
                  />
                </div>

                <div style={{ marginBottom: spacing.base }}>
                  <Text style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                    URL прод Jira
                  </Text>
                  <Input
                    placeholder="https://jira.moscow.alfaintra.net"
                    value={jiraUrl}
                    onChange={(e) => setJiraUrl(e.target.value)}
                    data-testid="jira-base-url-input"
                    style={{ marginTop: spacing.xs }}
                    addonBefore={
                      jiraMode === 'prod'
                        ? <span style={{ color: 'var(--color-success)', fontWeight: 600 }}>● АКТИВ</span>
                        : undefined
                    }
                    autoComplete="off"
                    data-lpignore="true"
                    data-1p-ignore="true"
                    data-form-type="other"
                    name="lens-jira-url-prod"
                    type="url"
                  />
                </div>
              </Card>
              </div>
            ),
          },
          {
            key: 'sso',
            label: (
              <span>
                <SafetyOutlined style={{ marginRight: spacing.xs }} />
                Вход и SSO
              </span>
            ),
            children: (
              <div style={{ maxWidth: SECTION_WIDTH }}>
              <Card
                title={
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: spacing.sm }}>
                    <IconBadge size={28}>
                      <SafetyOutlined />
                    </IconBadge>
                    Вход через Keycloak (SSO)
                  </span>
                }
                loading={loading}
                style={{
                  width: '100%',
                  marginBottom: spacing.base,
                  borderRadius: radius.lg,
                  boxShadow: shadows.card,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.base }}>
                  <Switch
                    checked={ssoEnabled}
                    onChange={setSsoEnabled}
                    data-testid="sso-enabled-switch"
                  />
                  <Text strong>Разрешить вход через AlfaProject</Text>
                </div>

                {ssoEnabled && !ssoConfigured && (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: spacing.base }}
                    message="Реквизиты клиента Keycloak заполнены не полностью"
                    description="Заполните Issuer, Client ID, Redirect URI и секрет клиента ниже. Без них кнопка входа и callback вернут 503."
                  />
                )}

                <div style={{ marginBottom: spacing.base }}>
                  <Text style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>Issuer (realm URL)</Text>
                  <Input
                    placeholder="https://id.alfaproject.pro/realms/alfa"
                    value={ssoIssuer}
                    onChange={(e) => setSsoIssuer(e.target.value)}
                    style={{ marginTop: spacing.xs }}
                    autoComplete="off"
                    data-testid="sso-issuer"
                    type="url"
                  />
                </div>

                <div style={{ display: 'flex', gap: spacing.base, flexWrap: 'wrap', marginBottom: spacing.base }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <Text style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>Client ID</Text>
                    <Input
                      placeholder="lens"
                      value={ssoClientId}
                      onChange={(e) => setSsoClientId(e.target.value)}
                      style={{ marginTop: spacing.xs }}
                      autoComplete="off"
                      data-testid="sso-client-id"
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <Text style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                      Client Secret {ssoHasClientSecret && <span style={{ color: 'var(--color-success)' }}>● задан</span>}
                    </Text>
                    <Input.Password
                      placeholder={ssoHasClientSecret ? '•••••• (оставьте пустым — не менять)' : 'секрет клиента'}
                      value={ssoClientSecret}
                      onChange={(e) => setSsoClientSecret(e.target.value)}
                      style={{ marginTop: spacing.xs }}
                      autoComplete="new-password"
                      data-testid="sso-client-secret"
                    />
                  </div>
                </div>

                <div style={{ marginBottom: spacing.base }}>
                  <Text style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                    Redirect URI (callback)
                  </Text>
                  <Input
                    placeholder="https://lens.alfaproject.pro/api/auth/sso/callback"
                    value={ssoRedirectUri}
                    onChange={(e) => setSsoRedirectUri(e.target.value)}
                    style={{ marginTop: spacing.xs }}
                    autoComplete="off"
                    data-testid="sso-redirect-uri"
                    type="url"
                  />
                  <Text style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
                    Должен совпадать с зарегистрированным в клиенте Keycloak. Путь: <Text code>/api/auth/sso/callback</Text>.
                  </Text>
                </div>

                <div style={{ marginBottom: spacing.lg }}>
                  <Text style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                    Post-logout redirect URI (опц.)
                  </Text>
                  <Input
                    placeholder="https://lens.alfaproject.pro/"
                    value={ssoPostLogoutUri}
                    onChange={(e) => setSsoPostLogoutUri(e.target.value)}
                    style={{ marginTop: spacing.xs }}
                    autoComplete="off"
                    data-testid="sso-post-logout-uri"
                    type="url"
                  />
                </div>

                <div style={{ marginBottom: spacing.base }}>
                  <Text style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                    Каталог ролей хаба (client-роли Keycloak)
                  </Text>
                  <Select
                    mode="tags"
                    value={ssoRoleCatalog}
                    onChange={setSsoRoleCatalog}
                    placeholder="lens-access, lens-admin, hermes-access…"
                    style={{ width: '100%', marginTop: spacing.xs }}
                    tokenSeparators={[',', ' ']}
                    data-testid="sso-role-catalog"
                  />
                  <Text style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
                    Полный список ролей. Введите имя и Enter, чтобы добавить.
                  </Text>
                </div>

                <div style={{ display: 'flex', gap: spacing.base, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <Text style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                      Роль доступа в Lens
                    </Text>
                    <Select
                      value={ssoAccessRole || undefined}
                      onChange={setSsoAccessRole}
                      placeholder="Выберите роль"
                      options={ssoRoleCatalog.map((r) => ({ label: r, value: r }))}
                      style={{ width: '100%', marginTop: spacing.xs }}
                      data-testid="sso-access-role"
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <Text style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                      Вторая роль доступа
                    </Text>
                    <Select
                      value={ssoAdminRole || undefined}
                      onChange={setSsoAdminRole}
                      placeholder="Выберите роль"
                      options={ssoRoleCatalog.map((r) => ({ label: r, value: r }))}
                      style={{ width: '100%', marginTop: spacing.xs }}
                      data-testid="sso-admin-role"
                    />
                  </div>
                </div>

                <Alert
                  type="info"
                  showIcon
                  style={{ marginTop: spacing.base }}
                  message="Доступ выдаётся ролью, а не группой"
                  description={
                    <span>
                      Гейт проверяет <Text code>{ssoAccessRole || 'lens-access'}</Text> в{' '}
                      <Text code>resource_access.lens.roles</Text> токена; наличие{' '}
                      <Text code>{ssoAdminRole || 'lens-admin'}</Text> тоже пускает в Lens, но
                      администратором системы не делает — его назначают вручную в разделе
                      «Пользователи». Роли назначаются группам в Keycloak.
                    </span>
                  }
                />
              </Card>
              </div>
            ),
          },
          {
            key: 'users',
            label: (
              <span>
                <TeamOutlined style={{ marginRight: spacing.xs }} />
                Пользователи
              </span>
            ),
            children: (
              <div style={{ maxWidth: SECTION_WIDTH }}>
              {!loading && (
                <KeycloakUsersCard ssoConfigured={ssoConfigured} dirty={ssoFormDirty} />
              )}
              </div>
            ),
          },
          {
            key: 'llm',
            label: (
              <span>
                <AiSparkIcon style={{ marginRight: spacing.xs }} />
                AI / LLM
              </span>
            ),
            children: (
              <div style={{ maxWidth: SECTION_WIDTH }}>
              <Card
                title={
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: spacing.sm }}>
                    <IconBadge size={28}>
                      <AiSparkIcon />
                    </IconBadge>
                    AI / LLM
                  </span>
                }
                loading={loading}
                style={{
                  width: '100%',
                  marginBottom: spacing.base,
                  borderRadius: radius.lg,
                  boxShadow: shadows.card,
                }}
              >
                <div style={{ marginBottom: spacing.base }}>
                  <Text style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                    Base URL (OpenAI-compatible)
                  </Text>
                  <Input
                    placeholder="https://alfagen.moscow.alfaintra.net/continue-dev"
                    value={llmBaseUrl}
                    onChange={(e) => setLlmBaseUrl(e.target.value)}
                    data-testid="llm-base-url-input"
                    style={{ marginTop: spacing.xs }}
                    autoComplete="off"
                    data-lpignore="true"
                    data-1p-ignore="true"
                    data-form-type="other"
                    name="lens-llm-url"
                    type="url"
                  />
                  {settings?.llmBaseUrlEffective && settings.llmBaseUrlEffective !== llmBaseUrl && (
                    <Text style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
                      Текущий (env): {settings.llmBaseUrlEffective}
                    </Text>
                  )}
                </div>

                <div style={{ marginBottom: spacing.base }}>
                  <Text style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                    Модель
                  </Text>
                  <div style={{ display: 'flex', gap: spacing.sm, marginTop: spacing.xs }}>
                    <Select
                      showSearch
                      allowClear
                      placeholder="Выберите модель"
                      value={llmModel || undefined}
                      onChange={(val) => setLlmModel(val ?? '')}
                      loading={modelsLoading}
                      onDropdownVisibleChange={(open) => {
                        if (open && llmModels.length === 0) void loadModels();
                      }}
                      options={
                        llmModels.length > 0
                          ? llmModels.map((m) => ({ label: m, value: m }))
                          : llmModel
                            ? [{ label: llmModel, value: llmModel }]
                            : []
                      }
                      style={{ flex: 1 }}
                      data-testid="llm-model-select"
                    />
                    <Button onClick={loadModels} loading={modelsLoading}>
                      Обновить
                    </Button>
                  </div>
                  {settings?.llmModelEffective && settings.llmModelEffective !== llmModel && (
                    <Text style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
                      Текущая (env): {settings.llmModelEffective}
                    </Text>
                  )}
                </div>

                <Alert
                  type="info"
                  showIcon
                  style={{ marginTop: spacing.sm }}
                  message="API-ключ задаётся персонально"
                  description={
                    <span>
                      LLM-ключ у каждого пользователя свой — задайте его в{' '}
                      <Link to="/profile">своём профиле</Link>. Кнопки «Обновить» и «Тест LLM»
                      используют ваш персональный ключ.
                    </span>
                  }
                />

                <Space style={{ marginTop: spacing.base }}>
                  <Button
                    loading={llmPinging}
                    onClick={async () => {
                      setLlmPinging(true);
                      setLlmPingResult(null);
                      try {
                        const res = await pingLlmAdmin();
                        setLlmPingResult({ ok: true, model: res.model });
                      } catch (err: any) {
                        setLlmPingResult({ ok: false, error: err.message ?? 'Ошибка подключения' });
                      } finally {
                        setLlmPinging(false);
                      }
                    }}
                  >
                    Тест LLM
                  </Button>
                </Space>

                {llmPingResult && (
                  <div style={{ marginTop: spacing.md }}>
                    {llmPingResult.ok ? (
                      <Alert type="success" message={`LLM доступен. Модель: ${llmPingResult.model}`} />
                    ) : (
                      <Alert type="error" message={llmPingResult.error} />
                    )}
                  </div>
                )}
              </Card>
              </div>
            ),
          },
          {
            key: 'hermes',
            label: (
              <span>
                <ExperimentOutlined style={{ marginRight: spacing.xs }} />
                Гермес
              </span>
            ),
            children: (
              <div style={{ maxWidth: SECTION_WIDTH }}>
              <HermesStandsCard />
              </div>
            ),
          },
          {
            key: 'telegram',
            label: (
              <span>
                <SendOutlined style={{ marginRight: spacing.xs }} />
                Телеграм
              </span>
            ),
            children: (
              <div style={{ maxWidth: SECTION_WIDTH }}>
                <TelegramAccountsCard />
              </div>
            ),
          },
        ]}
      />

      {/* Сохранение общее на все разделы формы: настройки платформы — одна запись.
          На «Пользователях» и «Гермесе» сохранять нечего — там свои действия. */}
      {FORM_TABS.includes(activeTab) && (
        <div style={{ marginTop: spacing.base, marginLeft: TAB_RAIL_WIDTH + TAB_CONTENT_GAP }}>
          <Button
            type="primary"
            onClick={handleSave}
            loading={saving}
            data-testid="platform-settings-save"
          >
            Сохранить
          </Button>
        </div>
      )}
    </div>
  );
}
